import {
  getComponentsData,
  getNumericFieldsForWorkspace,
  toComponentDataPointModel,
  createBubbleChartFieldNotifiers,
  createOnChangeFieldsClickHandler,
  createOnChangeBubbleSizeFieldHandler,
  createOnShowSizeZeroBubblesHandler,
  getDiffMode,
} from '../util';
import { ComponentsState } from 'streams/components/components$';
import { FieldsState } from 'streams/fields/fields$';
import { componentOperations } from '@ardoq/core';
import type {
  BubbleChartViewInterface,
  BubbleChartViewModelSourceData,
  BubbleChartViewSettings,
} from '@ardoq/bubble-chart';
import {
  bubbleChartOperations,
  FieldSource,
  getAxisTitlesForComponentChart,
  INITIAL_SOURCE_DATA_STATE,
  resolveIncludeAllDescendants,
  resolveRadiusField,
  sortDataPointsByCluster,
} from '@ardoq/bubble-chart';
import type {
  ContextShape,
  GraphModelShape,
  ScopeDiffState,
} from '@ardoq/data-model';
import { isTruthy, returnZero } from '@ardoq/common-helpers';
import { toIds } from '@ardoq/core';
import { uniq } from 'lodash';
import { linkedComponentToReferenceInfo } from '../referenceBubbleChart/viewModel$';
import { resetReferenceBubbleChart } from '../referenceBubbleChart/resetReferenceBubbleChart';
import { ArdoqId, DiffMode } from '@ardoq/api-types';
import { ActiveFilterState } from 'streams/filters/types';
import {
  BubbleChartSpecificInterface,
  getBubbleChartSpecificInterfaceWithModelInterfaces,
} from '../bubbleChartSpecificInterface';
import {
  getVisualizationInterface,
  getComponentLabelParts,
  truncateComponentLabel,
} from '@ardoq/graph';
import { isPresentationMode } from 'env';
import { simpleNotifier } from '@ardoq/dropdown-menu';
import { OBJECT_CONTEXT_MENU_NAME } from '@ardoq/context-menu';
import { ARDOQ_DEFAULT_LOCALE, Locale } from '@ardoq/locale';
import { type I18nLocale } from '@ardoq/i18n';
import { PermissionContext } from '@ardoq/access-control';
import { onViewSettingsUpdate } from 'tabview/onViewSettingsUpdate';
import { createBubbleChartPopovers } from '../popovers';
import { getBubbleChartTrackingCallbacks } from '../streams/performanceTrackingUtils';
import {
  getSelectedQuadrantDropdownLabels,
  getTimeLayoutLabels,
} from 'locales/getBubbleChartLabels';

type ResetArgs = Pick<
  BubbleChartViewModelSourceData,
  | 'numericFields'
  | 'availableIncomingReferenceTypeNames'
  | 'availableOutgoingReferenceTypeNames'
> & {
  context: ContextShape;
  components: ComponentsState;
  viewSettings: BubbleChartViewSettings;
  locale: Locale;
};
const reset = (
  previousState: BubbleChartViewModelSourceData,
  {
    context,
    components: componentsState,
    viewSettings,
    numericFields,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    locale,
  }: ResetArgs
): BubbleChartViewModelSourceData => {
  const componentNumericFields = numericFields.filter(
    attributes => attributes.componentType.length
  );
  const {
    selectedFieldNameX,
    selectedFieldNameY,
    selectedFieldNameRadius,
    showBubblesWithZeroValue: viewSettingsShowBubblesWithZeroValue,
  } = viewSettings;

  const viewInterface = previousState.viewInterface;

  const showBubblesWithZeroValue =
    viewSettingsShowBubblesWithZeroValue === undefined ||
    viewSettingsShowBubblesWithZeroValue;

  const [xField, yField, radiusField] = [
    selectedFieldNameX,
    selectedFieldNameY,
    selectedFieldNameRadius,
  ].map(field => (typeof field === 'string' ? field : ''));
  const { components, emptyFields } = numericFields.length
    ? getComponentsData(
        componentsState,
        viewInterface,
        context,
        xField,
        yField,
        radiusField,
        viewSettings.includeAllDescendants
      )
    : {
        components: [],
        emptyFields: {
          xHasValues: !xField,
          yHasValues: !yField,
          radiusHasValues: !radiusField,
        },
      };

  const { xAxisTitle, yAxisTitle } = getAxisTitlesForComponentChart(
    viewSettings,
    numericFields,
    locale
  );

  const labelsData: Map<ArdoqId, string> = new Map(
    components.map(componentId => [
      componentId,
      truncateComponentLabel({
        ...getComponentLabelParts(componentId, viewInterface),
        measure: returnZero,
        width: Infinity,
      }),
    ])
  );

  const displayColors = new Map(
    components.map(componentId => [
      componentId,
      viewInterface.getComponentDisplayColor(componentId, {
        useAsBackgroundStyle: false,
      }),
    ])
  );

  const unfilteredData = components.map(componentId =>
    toComponentDataPointModel({
      viewInterface,
      highlightedComponent: context.componentId,
      componentId,
      selectedFieldNameX: xField,
      selectedFieldNameY: yField,
      selectedFieldNameRadius: radiusField,
      labelsData,
      displayColors,
    })
  );

  const unsortedData = unfilteredData.filter(
    bubble => (showBubblesWithZeroValue || bubble.radius) && bubble
  );

  const data = sortDataPointsByCluster(unsortedData);

  const isFilteredByRadius = Boolean(
    !viewSettings.showBubblesWithZeroValue &&
    !unsortedData.length &&
    unfilteredData.length
  );

  return {
    ...previousState,
    numericFields: componentNumericFields,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    isLoadingData: !context.workspaceId,
    xAxisTitle,
    yAxisTitle,
    data,
    viewSettings,
    emptyFields,
    isFilteredByRadius,
  };
};

type GetWorkspaceModeSourceDataArgs = {
  viewInterface: BubbleChartViewInterface;
  context: ContextShape;
  graph: GraphModelShape;
  viewSettings: BubbleChartViewSettings;
  permissionContext: PermissionContext;
  components: ComponentsState;
};
const getWorkspaceModeSourceData = ({
  viewInterface,
  context,
  graph,
  viewSettings,
  permissionContext,
  components,
}: GetWorkspaceModeSourceDataArgs) => {
  const { includeAllDescendants } = viewSettings;

  const numericFields = getNumericFieldsForWorkspace(
    viewInterface,
    permissionContext,
    context.workspaceId
  );

  const { sourceMap, targetMap } = graph;
  const { componentId, workspaceId } = context;

  /**
   * Unless we are dealing with ref bubble chart, we don't care about contextComponents
   */
  const contextRoots = componentId
    ? [components.byId[componentId]].filter(isTruthy)
    : componentOperations.getRootComponents(workspaceId, components.models);

  const resolvedIncludeAllDescendants = resolveIncludeAllDescendants(
    includeAllDescendants
  );
  const referenceBubbleChartStartSet =
    resolvedIncludeAllDescendants === 'none'
      ? toIds(contextRoots)
      : resolvedIncludeAllDescendants === 'all'
        ? contextRoots.flatMap(root =>
            toIds(
              componentOperations.getFlatHierarchy(
                [root],
                components.childrenByParent
              )
            )
          )
        : contextRoots.flatMap(root => [
            root._id,
            ...toIds(components.childrenByParent.get(root._id) ?? []),
          ]);

  const [incomingLinkedComponents, outgoingLinkedComponents] = [
    targetMap,
    sourceMap,
  ].map(referenceMap =>
    referenceBubbleChartStartSet.flatMap(
      componentId => referenceMap.get(componentId) ?? []
    )
  );

  const [incomingReferences, outgoingReferences] = [
    incomingLinkedComponents,
    outgoingLinkedComponents,
  ].map(linkedComponents =>
    linkedComponents
      .map(id => linkedComponentToReferenceInfo(viewInterface, id))
      .filter(isTruthy)
  );

  const [
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
  ] = [incomingReferences, outgoingReferences].map(references =>
    uniq(references?.map(({ referenceTypeName }) => referenceTypeName))
  );

  return {
    numericFields,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    incomingReferences,
    outgoingReferences,
  };
};

export type WorkspaceModeBubbleChartSourceDataArgs = {
  context: ContextShape;
  components: ComponentsState;
  viewSettings: BubbleChartViewSettings;
  graph: GraphModelShape;
  scopeDiffState: ScopeDiffState;
  activeDiffMode: DiffMode;
  activeFilterState: ActiveFilterState;
  permissionContext: PermissionContext;
  locale: Locale;
  i18nLocale: I18nLocale;
  /** Not read when computing the source data; recomputes it on field changes. */
  fields: FieldsState;
  /** Duration tracking session started when the source data args were emitted. */
  trackingId?: string;
};

export const buildWorkspaceModeViewModelSourceData = (
  previousState: BubbleChartViewModelSourceData,
  {
    context,
    components,
    viewSettings,
    graph,
    scopeDiffState: { scopeDiff },
    activeDiffMode,
    activeFilterState: { activeFilters },
    permissionContext,
    locale,
    i18nLocale,
    trackingId,
  }: WorkspaceModeBubbleChartSourceDataArgs
): BubbleChartViewModelSourceData => {
  const selectBackgroundNotifier =
    previousState.popovers.disabledZoomToQuadrantPopover
      .selectBackgroundNotifier;
  // #region workspace mode
  const {
    numericFields,
    availableIncomingReferenceTypeNames,
    availableOutgoingReferenceTypeNames,
    incomingReferences,
    outgoingReferences,
  } = getWorkspaceModeSourceData({
    viewInterface: previousState.viewInterface,
    context,
    graph,
    viewSettings,
    permissionContext,
    components,
  });

  const isWorkspaceEmpty = !components.models.some(component =>
    componentOperations.isRootComponent(context.workspaceId, component)
  );

  const newData =
    viewSettings.fieldSource === FieldSource.BY_COMPONENT
      ? reset(previousState, {
          context,
          viewSettings,
          numericFields,
          availableIncomingReferenceTypeNames,
          availableOutgoingReferenceTypeNames,
          locale,
          components,
        })
      : resetReferenceBubbleChart(previousState, {
          viewSettings,
          permissionContext,
          numericFields,
          availableIncomingReferenceTypeNames,
          availableOutgoingReferenceTypeNames,
          incomingReferences,
          outgoingReferences,
        });

  const { isScopeDiffMode, activeDiffMode: resolvedActiveDiffMode } =
    getDiffMode(scopeDiff, activeDiffMode);

  const trackingCallbacks = trackingId
    ? getBubbleChartTrackingCallbacks(trackingId, newData.data)
    : undefined;

  const hasOnlyPointsWithZeroRadius = Boolean(newData.isFilteredByRadius);

  const emptyStateData = bubbleChartOperations.getEmptyStateData({
    numericFields: newData.numericFields,
    allNumericFields: newData.numericFields,
    data: newData.data,
    isWorkspaceEmpty,
    viewSettings,
    isPresentationMode: previousState.isPresentationMode,
    hasActiveFilter: Boolean(activeFilters?.componentRules.rules?.length),
    hasOnlyPointsWithZeroRadius,
    isViewLoading: !context.workspaceId,
  });

  const onChangeFieldsClick = emptyStateData.hasFieldsSelected
    ? createOnChangeFieldsClickHandler({
        fieldNotifiers: previousState.fieldNotifiers,
        emptyFields: newData.emptyFields,
      })
    : undefined;

  const onChangeBubbleSizeField = emptyStateData.hasOnlyPointsWithZeroRadius
    ? createOnChangeBubbleSizeFieldHandler({
        fieldNotifiers: previousState.fieldNotifiers,
      })
    : undefined;

  const onShowSizeZeroBubbles = emptyStateData.hasOnlyPointsWithZeroRadius
    ? createOnShowSizeZeroBubblesHandler('bubble')
    : undefined;

  return {
    ...newData,
    isScopeDiffMode,
    emptyStateData: {
      ...emptyStateData,
      onChangeFieldsClick,
      onChangeBubbleSizeField,
      onShowSizeZeroBubbles,
    },
    activeDiffMode: resolvedActiveDiffMode,
    locale,
    i18nLocale,
    selectedQuadrantDropdownLabels:
      getSelectedQuadrantDropdownLabels(i18nLocale),
    timeLayoutLabels: getTimeLayoutLabels(i18nLocale),
    popovers: createBubbleChartPopovers(i18nLocale, {
      selectBackgroundNotifier,
      disabledZoomToQuadrantOnLinkClick: () => {
        selectBackgroundNotifier?.notify();
      },
    }),
    radiusFieldData: resolveRadiusField(
      newData.numericFields,
      newData.viewSettings.selectedFieldNameRadius
    ),
    viewInterface: {
      ...newData.viewInterface,
      trackingCallbacks,
    },
  };
};

export const getInitialWorkspaceModeSourceDataState = (
  viewInstanceId: string
): BubbleChartViewModelSourceData => {
  const selectBackgroundNotifier = simpleNotifier();

  return {
    ...INITIAL_SOURCE_DATA_STATE,
    viewInstanceId,
    viewInterface: {
      onViewSettingsUpdate,
      contextMenuName: OBJECT_CONTEXT_MENU_NAME,

      ...getVisualizationInterface<BubbleChartSpecificInterface>({
        getViewSpecificInterface:
          getBubbleChartSpecificInterfaceWithModelInterfaces,
      }),
    },
    isPresentationMode: isPresentationMode(),
    popovers: createBubbleChartPopovers(INITIAL_SOURCE_DATA_STATE.i18nLocale, {
      selectBackgroundNotifier,
      disabledZoomToQuadrantOnLinkClick: () => {
        selectBackgroundNotifier.notify();
      },
    }),
    fieldNotifiers: createBubbleChartFieldNotifiers(),
    locale: ARDOQ_DEFAULT_LOCALE,
  };
};
